/**
 * Will filter out files that are the same file name and size from the list of existing files.
 *
 * @param {File[]} files The files selected for upload
 * @param {File[]} existingFilesList An array of the files already selected for upload
 * @returns {File[]}
 */
const getUniqueFiles = (files: File[], existingFilesList: File[]): File[] => {
  const stringifiedExistingFilesList = existingFilesList.map((file) => `${file.name}+${file.size}`);
  return files.filter(({ name, size }) => !stringifiedExistingFilesList.includes(`${name}+${size}`));
};

/**
 * Formats a file type to a human-readable string.
 *
 * @param {string[]} fileTypes The file types to format.
 * @returns {string} The formatted file type.
 */
function formatFileTypes(fileTypes: string[]) {
  const fileTypeLength = fileTypes?.length ?? 0;

  return fileTypes
    ?.map((fileType: string) => fileType.split('/')[1].toUpperCase())
    .join(fileTypeLength === 2 ? ' or ' : ', ')
    .replace(/, ([^,]*)$/, ', or $1');
}

/**
 * Formats a file size to a human-readable string.
 *
 * @param {number} size The size of the file in bytes.
 * @param {number} [decimals=2] The number of decimal places to show.
 * @returns {string} The formatted file size.
 */
const formatFileSize = (size: number, decimals = 2) => {
  if (size <= 0) return '0 B';

  // Sizes less than 1024 bytes we do not want to show a decimal
  if (size < 1024) return `${size} B`;

  const i = Math.floor(Math.log(size) / Math.log(1024));
  const sizeValue = (size / 1024 ** i).toFixed(decimals).replace(/\.0+$/, '');
  const sizeUnit = ['B', 'KB', 'MB', 'GB', 'TB'][i];

  return `${sizeValue} ${sizeUnit}`;
};

export { formatFileTypes, formatFileSize, getUniqueFiles };
